import {
  mobile,
  styled,
  Text,
  useMutation,
  useRouter,
  useTranslation,
} from '@obvio/app'
import { SvgCloseMenu } from '@obvio/svg'
import { Button, Modal, Stack, Submit, SubmitState, TextInput } from '@obvio/ui'
import { AnimatePresence } from 'framer-motion'
import { useCallback, useMemo, useState } from 'react'

import { CenteredSection } from '../CenteredSection'
import { FadeIn } from '../FadeIn'
import { ModalInputs, ModalTitle } from '../Modal'

import type { ReactElement } from 'react'

type UseModal = {
  open: boolean
  show: () => void
  hide: () => void
}

const SubmitWrap = styled.div`
  margin: ${(theme) => theme.spacing.large} auto !important;
  button {
    padding-left: calc(${(theme) => theme.spacing.extraLarge} * 1.5);
    padding-right: calc(${(theme) => theme.spacing.extraLarge} * 1.5);
  }
  @media ${mobile} {
    width: 100%;
    button {
      width: 100%;
    }
  }
`

const OffsetModalInputs = styled(ModalInputs)`
  margin-top: ${(theme) => theme.spacing.extraLarge} !important;
  margin-bottom: ${(theme) => theme.spacing.small};
`

const CenterButton = styled(Button)`
  margin-left: auto !important;
`

// TODO: Maybe this could be a part of ui lib?
export const useModal = (name: string): UseModal => {
  const { hasParam, setParam, removeParam } = useRouter()
  const show = useCallback(() => {
    void setParam(name)
  }, [name, setParam])
  const hide = useCallback(() => {
    void removeParam(name)
  }, [name, removeParam])
  const open = hasParam(name)

  return useMemo(() => ({ open, show, hide }), [open, show, hide])
}

const MODAL_PARAM = 'register-modal'

export function RegisterModal(): ReactElement {
  const [registered, setRegistered] = useState(false)
  const [register] = useMutation('registerOpen')
  const { open, hide } = useModal(MODAL_PARAM)

  const { t } = useTranslation()

  const handleSubmit = (data: {
    name: string
    email: string
    phone: string
  }) => {
    void register(data)
    setRegistered(true)
    return SubmitState.OK
  }

  return (
    <AnimatePresence>
      {open && (
        <Modal onSubmit={handleSubmit} CloseIcon={SvgCloseMenu} onClose={hide}>
          {registered ? (
            <FadeIn delay={0} y={25}>
              <CenteredSection>
                <Stack kind="vertical" spacing="large">
                  <ModalTitle tag="h2">Registered</ModalTitle>
                  <CenterButton
                    kind="ghost"
                    onClick={() => setRegistered(false)}
                  >
                    {t('event.registerMore')}
                  </CenterButton>
                </Stack>
              </CenteredSection>
            </FadeIn>
          ) : (
            <FadeIn delay={0} y={25}>
              <Stack kind="vertical">
                <ModalTitle tag="h2">
                  Get an alert on the grand opening
                </ModalTitle>
                <Text>
                  Leave us your email and we will notify you of the grand
                  opening of the gallery website
                </Text>
                <OffsetModalInputs kind="vertical">
                  <TextInput name="email" label="E-MAIL ADDRESS" />
                  <TextInput name="phone" label="PHONE NUMBER" />
                </OffsetModalInputs>
                <SubmitWrap>
                  <Submit kind="ghost">GET AN ALERT</Submit>
                </SubmitWrap>
              </Stack>
            </FadeIn>
          )}
        </Modal>
      )}
    </AnimatePresence>
  )
}

RegisterModal.PARAM = MODAL_PARAM
